import { type ApplicationStatus } from './application-status'

/** Кто меняет статус. `system` — api и фоновые задачи без участия человека. */
export const ACTORS = ['client', 'operator', 'watcher', 'system'] as const
export type Actor = (typeof ACTORS)[number]

/**
 * Подтверждение записи из кабинета. Нажатие кнопки «записаться» им не является:
 * watcher перечитывает кабинет и присылает то, что там увидел.
 */
export interface BookingEvidence {
  readonly verifiedInCabinet: true
  readonly verifiedAt: Date
  /** Дата и время собеседования, как показал кабинет. */
  readonly appointmentAt: Date
}

export interface TransitionContext {
  readonly actor: Actor
  readonly reason?: string
  readonly evidence?: BookingEvidence
}

type Requirement = 'reason' | 'evidence'

interface Rule {
  readonly from: readonly ApplicationStatus[]
  readonly to: ApplicationStatus
  readonly by: readonly Actor[]
  readonly requires?: Requirement
}

/** Статусы, из которых можно уйти в ветку или остановить поиск. `booking` сюда не входит намеренно. */
const INTERRUPTIBLE = [
  'created',
  'access_verified',
  'awaiting_payment',
  'searching',
  'slot_found',
] as const satisfies readonly ApplicationStatus[]

const BRANCHES = ['needs_client', 'needs_operator', 'paused'] as const satisfies readonly ApplicationStatus[]

/** Куда возвращается заявка из ветки, когда причина снята. */
const RESUME_TARGETS = ['created', 'access_verified', 'awaiting_payment', 'searching'] as const

const RULES: readonly Rule[] = [
  // основной путь
  { from: ['created'], to: 'access_verified', by: ['watcher'] },
  { from: ['access_verified'], to: 'awaiting_payment', by: ['system'] },
  // постоплата: поиск стартует без оплаты
  { from: ['access_verified', 'awaiting_payment'], to: 'searching', by: ['system'] },
  { from: ['searching'], to: 'slot_found', by: ['watcher'] },
  { from: ['slot_found'], to: 'booking', by: ['watcher'] },
  // дата ушла или не подошла
  { from: ['slot_found'], to: 'searching', by: ['watcher'] },
  // «записан» только с подтверждением из кабинета, в том числе вручную оператором
  { from: ['booking'], to: 'booked', by: ['watcher'], requires: 'evidence' },
  { from: ['needs_operator'], to: 'booked', by: ['operator'], requires: 'evidence' },
  // кабинет однозначно показал, что записи нет
  { from: ['booking'], to: 'searching', by: ['watcher'], requires: 'reason' },
  // неясный результат: повтор заблокирован, решает оператор
  { from: ['booking'], to: 'needs_operator', by: ['watcher', 'system'], requires: 'reason' },
  { from: ['booked'], to: 'completed', by: ['system', 'operator'] },
  { from: ['booked'], to: 'needs_operator', by: ['system', 'operator'], requires: 'reason' },

  // ветки
  { from: INTERRUPTIBLE, to: 'needs_client', by: ['watcher', 'system', 'operator'], requires: 'reason' },
  { from: INTERRUPTIBLE, to: 'needs_operator', by: ['watcher', 'system', 'operator'], requires: 'reason' },
  { from: INTERRUPTIBLE, to: 'paused', by: ['client', 'watcher', 'system', 'operator'], requires: 'reason' },
  { from: BRANCHES, to: 'needs_client', by: ['system', 'operator'], requires: 'reason' },
  { from: BRANCHES, to: 'needs_operator', by: ['client', 'system', 'operator'], requires: 'reason' },
  { from: BRANCHES, to: 'paused', by: ['system', 'operator'], requires: 'reason' },
  ...RESUME_TARGETS.map((to): Rule => ({ from: BRANCHES, to, by: ['system', 'operator'], requires: 'reason' })),

  // остановка: клиент сам, оператор или истечение срока поиска
  {
    from: [...INTERRUPTIBLE, ...BRANCHES],
    to: 'stopped',
    by: ['client', 'operator', 'system'],
    requires: 'reason',
  },
]

export type TransitionErrorCode =
  'same_status' | 'transition_not_allowed' | 'actor_not_allowed' | 'reason_required' | 'evidence_required'

export type TransitionResult = { readonly ok: true } | { readonly ok: false; readonly code: TransitionErrorCode }

function rulesFor(from: ApplicationStatus, to: ApplicationStatus): Rule[] {
  return RULES.filter((r) => r.to === to && r.from.includes(from))
}

function satisfies(rule: Rule, ctx: TransitionContext): TransitionErrorCode | null {
  if (rule.requires === 'reason' && (ctx.reason === undefined || ctx.reason.trim() === '')) return 'reason_required'
  if (rule.requires === 'evidence' && ctx.evidence?.verifiedInCabinet !== true) return 'evidence_required'
  return null
}

/** Проверяет переход. Не бросает: вызывающий сам превращает код в ошибку своего слоя. */
export function checkTransition(
  from: ApplicationStatus,
  to: ApplicationStatus,
  ctx: TransitionContext,
): TransitionResult {
  if (from === to) return { ok: false, code: 'same_status' }
  const candidates = rulesFor(from, to)
  if (candidates.length === 0) return { ok: false, code: 'transition_not_allowed' }
  const byActor = candidates.filter((r) => r.by.includes(ctx.actor))
  const [first] = byActor
  if (first === undefined) return { ok: false, code: 'actor_not_allowed' }
  for (const rule of byActor) {
    if (satisfies(rule, ctx) === null) return { ok: true }
  }
  return { ok: false, code: satisfies(first, ctx) ?? 'transition_not_allowed' }
}

/** Куда этот участник может перевести заявку из статуса: для кнопок админки и проверок. */
export function nextStatuses(from: ApplicationStatus, actor: Actor): ApplicationStatus[] {
  const out = new Set<ApplicationStatus>()
  for (const rule of RULES) {
    if (rule.from.includes(from) && rule.by.includes(actor) && rule.to !== from) out.add(rule.to)
  }
  return [...out]
}
